import React from 'react';
import API from '@API/index';
import { useDispatch, useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import { setStreamList } from '@store/streams/actions';
import { flatten, isEqual, sortBy } from 'lodash';
import { setPermissions } from '@store/session/actions';
import { AssetsPermissions } from '@store/session/types';

/**
 * Hook that will perform an update to Redux Store (Streams) when called.
 * This hook is useful when you want to check for updates on backend and update the App State at the same time.
 * @param siteId ID of the site to perform the update.
 * @param deps Dependencies to trigger the update logic.
 * 
 * @example
 * const Component: React.FC<{}> = () => {
 *  // Updates Sites every time this component mounts.
 *  useUpdateStreams("forsight_company-staging1");
 * }
 * 
 * const Component: React.FC<{}> = () => {
 *  const [selectedSiteId, setSelectedSiteId] = React.useState<string>("forsight_company-staging1");
 *  const [selectedTab, setSelectedTab] = React.useState<number>(0);
 * 
 *  // Update Sites every time the selectedTab state changes.
 *  useUpdateStreams(selectedSiteId, [selectedTab])
 * }
 */
function useUpdateStreams(
  siteId?: string,
  companyId?: string,
  role?: string | Record<string, Record<string, string[]>>,
  userId?: string,
  deps?: React.DependencyList
) {
  const dispatch = useDispatch();
  const streams = useSelector((store: IStoreState) => store.streams.streams);
  const streamsRef = React.useRef(streams);

  const [loading, setLoading] = React.useState<boolean>(true);
  const companies = useSelector((store: IStoreState) => store.companies.companies);

  const companiesRef = React.useRef(companies)

  React.useEffect(() => { streamsRef.current = streams }, [streams])
  React.useEffect(() => { companiesRef.current = companies }, [companies]);

  const perform = React.useCallback(() => {
    if (siteId || companyId) {
      setLoading(true);
      API.Streams.listStreams(siteId, companyId)
        .then((result: VideoStream[]) => {
          const filtered = streamsRef.current.filter(el => el.siteId !== siteId);
          const final = siteId ? filtered.concat(result) : result;
          if (isEqual(sortBy(final, ['kvsName']), sortBy(streamsRef.current, ['kvsName'])) === false) {
            dispatch(setStreamList(final));
          }

          return getPermissions(final, role, userId, companyId, siteId);
        })
        .then((permissions) => {
          dispatch(setPermissions(permissions));
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false);
          }, 40)
        })
    } else {
      setLoading(true);

      const fetchWithRetry = async (companyId: string, retries = 3, delayMs = 1000): Promise<VideoStream[]> => {
        for (let attempt = 0; attempt <= retries; attempt++) {
          try {
            return await API.Streams.listStreams(undefined, companyId);
          } catch (e) {
            if (attempt < retries) await new Promise(res => setTimeout(res, delayMs * Math.pow(2, attempt)));
            else return [];
          }
        }
        return [];
      };

      const fetchInBatches = async (): Promise<VideoStream[]> => {
        const companies = companiesRef.current;
        const BATCH_SIZE = 5;
        const allStreams: VideoStream[] = [];
        for (let i = 0; i < companies.length; i += BATCH_SIZE) {
          const batch = companies.slice(i, i + BATCH_SIZE);
          const results = await Promise.all(batch.map(el => fetchWithRetry(el.companyId)));
          allStreams.push(...flatten(results));
        }
        return allStreams;
      };

      fetchInBatches()
        .then((allStreams) => {
          dispatch(setStreamList(allStreams));

          // Multi-company mode is super admin only - grant ALL permissions
          const allPermissions: AssetsPermissions = {};
          allStreams.forEach(stream => allPermissions[stream.kvsName] = ["ALL"]);
          dispatch(setPermissions(allPermissions));
        })
        .catch((e) => {
          console.log(e)
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false);
          }, 40)
        })
    }
  }, [dispatch, role, siteId, userId, companyId])

  React.useEffect(perform, [...(deps || []), siteId, perform])
  return loading;
}

export async function getPermissions(
  streams: VideoStream[],
  role?: string | Record<string, Record<string, string[]>>,
  userId?: string,
  companyId?: string,
  siteId?: string,
): Promise<AssetsPermissions> {
  try {
    const permissions: AssetsPermissions = {};

    if (role === 'superadmin' || role === 'admin') {
      streams.forEach(stream => permissions[stream.kvsName] = ["ALL"]);
    } else if (siteId === undefined && companyId !== undefined) {
      const apiPermissions = await API.Permissions.listPermissionsForCompany(userId || "", companyId)
      apiPermissions.forEach((element: any) => permissions[element.kvsName] = element.permissions);
    } else if (siteId !== undefined && companyId !== undefined) {
      const apiPermissions = await API.Permissions.listPermissionsForSite(userId || "", companyId, siteId)
      apiPermissions.forEach((element: any) => permissions[element.kvsName] = element.permissions);
    }

    return permissions;
  } catch (e) {
    throw e;
  }
}

export default useUpdateStreams;
